import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IconsModule, KENDO_ICONS, SVGIcon } from '@progress/kendo-angular-icons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ListViewDataResult, ListViewModule } from "@progress/kendo-angular-listview";
import { ButtonsModule, KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_LABELS, LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule, KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { globeIcon, thumbUpIcon } from '@progress/kendo-svg-icons';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { KENDO_EDITOR } from '@progress/kendo-angular-editor';
import { GridModule } from '@progress/kendo-angular-grid';
import { PagerModule } from '@progress/kendo-angular-pager';
import { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { IconName, IconPrefix, IconProp } from '@fortawesome/fontawesome-svg-core';
import { KENDO_TOOLBAR } from '@progress/kendo-angular-toolbar';
import { DialogCloseResult, DialogModule, DialogService } from '@progress/kendo-angular-dialog';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { ConfirmModalComponent } from '../../common-components/confirm-modal/confirm-modal.component';
import { CasiKendoEditorComponent } from '../../common-components/casi-kendo-editor/casi-kendo-editor.component';
import { EditorImageUploadComponent } from '../../common-components/editor-image-upload/editor-image-upload.component';
import {
  CasiOwnerUser,
  CasiProjectLabel,
  CasiStoryEstimate, CasiStoryPriority, CasiStoryStatus,
  CasiStoryType, CasiUserStory, CasiUserStoryComment, Project, User as UserModel
} from '../../models/models.lib';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { AuthTokenService } from '../../auth/auth-token.service';
import { UserStoryCommentsService } from '../../tracker/user-story-comments.service';
import { UserStoryService } from '../../tracker/user-story.service';
import { UserStoryOwnerService } from '../../tracker/user-story-owner.service';
import { CasiCloseOptions, CommentSortOptions } from '../../common/constants.lib';
import { UserStoryCommentComponent } from '../user-story-comment/user-story-comment.component';
import { TokenSwapPipe } from '../../pipes/token-swap.pipe';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { ImgZoomPipe } from '../../pipes/img-zoom.pipe';
import { StatusButtonComponent } from '../../common-components/status-button/status-button.component';
import { ProjectUserService } from '../../tracker/project-user.service';

const { apiUrl } = environment;

export const inlineEditTypes = {
  description: 'description',
  code: 'code',
  notset: 'notset'
};

@Component({
  selector: 'casi-user-story',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ListViewModule,
    LayoutModule,
    InputsModule,
    FormsModule,
    ReactiveFormsModule,
    IconsModule,
    ButtonsModule,
    InputsModule,
    LabelModule,
    DropDownsModule,
    AutoFocusDirective,
    FontAwesomeModule,
    GridModule,
    PagerModule,
    KENDO_EDITOR,
    KENDO_DROPDOWNS, KENDO_LABELS, KENDO_ICONS,
    KENDO_BUTTONS,
    KENDO_TOOLBAR,
    ConfirmModalComponent,
    CasiKendoEditorComponent,
    EditorImageUploadComponent,
    UserStoryCommentComponent,
    TokenSwapPipe,
    SafeHtmlPipe,
    StatusButtonComponent,
    ImgZoomPipe
    //PipesModule
  ],
  templateUrl: './user-story.component.html',
  styleUrl: './user-story.component.scss'
})
export class UserStoryComponent implements OnInit, OnDestroy, AfterContentInit, AfterContentInit {
  private _userStory: CasiUserStory | undefined;
  @Input({ required: true })
  set userStory(val: CasiUserStory) {
    this._userStory = val;
    this.logger.debug(`input:userStory: `, this._userStory);
    this.formHeader.patchValue(this._userStory);
    this.form.patchValue(this._userStory);
    this.formDescription.patchValue(this._userStory);
    this.resetSelection();
  }
  get userStory() {
    return (this._userStory as CasiUserStory);
  } //public userStory!: UserStory;

  private _storyTypes: CasiStoryType[] = [];
  @Input({ required: true }) set storyTypes(val: CasiStoryType[]) {
    this._storyTypes = val;
    this.setStoryIcons();
  }
  get storyTypes() {
    return this._storyTypes;
  }
  @Input({ required: true }) storyStatuses: CasiStoryStatus[] = [];
  @Input({ required: true }) storyPriorities: CasiStoryPriority[] = [];
  @Input({ required: true }) storyEstimates: CasiStoryEstimate[] = [];
  @Input({ required: true }) users: UserModel[] = [];
  @Input() modUsers: CasiOwnerUser[] = [];
  @Input() selectedUserStoryId: string | null = null;
  @Input() showProjects: boolean = true;
  @Input({ required: true }) projects: Project[] = [];
  @Input() showCloseButton: boolean = true;

  private _projectLables: CasiProjectLabel[] = [];
  @Input({ required: true }) set projectLabels(val: CasiProjectLabel[]) {
    this.allProjectLabels = val;
    if (val) {
      this.setProjectLabels(val);
    } else {
      this.setProjectLabels([]);
    }
  }
  get projectLabels() {
    return this._projectLables;
  };

  @Output() refresh = new EventEmitter();

  @Output() submitForm: EventEmitter<FormGroup> = new EventEmitter();
  @Output() closeForm: EventEmitter<void> = new EventEmitter();

  @ViewChild("uploadDialog") public dialog: EditorImageUploadComponent = <EditorImageUploadComponent>{};

  public globeIcon: SVGIcon = globeIcon;

  //#region Drop down options

  public defaultDropdownItem: { text: string; id: number | undefined } = {
    text: "Select ...",
    id: undefined
  };

  public defaultStoryItem: { text: string; value: number | undefined } = {
    text: "Select ...",
    value: undefined
  };

  public defaultProjectItem: { project_name: string; id: number | undefined } = {
    project_name: "Select ...",
    id: undefined
  };

  public defaultStoryTypeItem: {
    text: string; id: number | null; user_story_type_description: string;
    iconClass: string | undefined; icon: string | undefined
  } = {
      text: "Select ...",
      id: null,
      user_story_type_description: '',
      iconClass: undefined,
      icon: undefined
    };

  public defaultRequesterItem: {
    text: string; id: number | null; fullName: string;
    initials: string;
  } = {
      text: "Select ...",
      id: null,
      fullName: '',
      initials: ''
    };

  //#endregion

  formHeader: FormGroup;
  form: FormGroup;
  formComment: FormGroup;
  formDescription: FormGroup;

  @ViewChild('pForm') pForm!: ElementRef;

  public selectedType: number | undefined; // = StoryTypes.Bug;
  public selectTypeIconClass: string | undefined;
  public selectedPriority: string | undefined; // = StoryPriorities.None;
  public selectedRequester: number | undefined;
  public selectedStatus: number | undefined;
  public selectedStoryStatus: CasiStoryStatus | undefined;
  public selectedProjectId: number | undefined;
  public selectedEstimate: number | undefined;
  public selectedOwners: any[] = []; // any = []; //ICasiOwner[] = [];
  // public modUsers: CasiOwnerUser[] = [];
  public edvalue = '';
  public comvalue = '';
  public historicFiles: any[] = [];
  //public ownerUsers: Array<{user_id: number, user_name: string, fullName: string}> = [];//ICasiOwner[] = []; //full list to select from
  public selectedSort: CommentSortOptions = CommentSortOptions.oldestToNewest; //  "Oldest to newest";

  public iconList: IconProp | undefined;
  public expandedState = false;
  public apiUrl: string = apiUrl;
  public thumbUpIcon: SVGIcon = thumbUpIcon;
  public view: Observable<CasiUserStoryComment[]> = new Observable<CasiUserStoryComment[]>();

  public readonlyDescription: boolean = true;
  public readonlyCode: boolean = true;

  private _rawData: BehaviorSubject<CasiUserStoryComment[]> = new BehaviorSubject<CasiUserStoryComment[]>([]);
  get rawData$() {
    return this._rawData.asObservable();
  }
  get rawData() {
    return this._rawData.getValue();
  }
  set rawData(val: CasiUserStoryComment[]) {
    this._rawData.next(val);
  }

  set userStoryProject(val: Project | undefined) {
    if (this._userStory) {
      // this._userStory.project_id = val?.id;
      this._userStory.project_name = val?.project_name
      // this.form.patchValue({ project_id: val?.id, project_name: val?.project_name });
      this.form.patchValue({ project_name: val?.project_name });
    }
  }

  public listView: ListViewDataResult | undefined;
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };

  public sortOptions: Array<CommentSortOptions> = [
    CommentSortOptions.newestToOldest,
    CommentSortOptions.oldestToNewest
  ];

  public sort: SortDescriptor[] = [
    {
      field: "name",
      dir: "asc",
    },
  ];

  private _subs: Subscription[] = [];
  private commentsFetched: boolean = false;
  private deletedOwner: boolean = false;
  private allProjectLabels: CasiProjectLabel[] = [];
  private eventsSet: boolean = false;

  constructor(
    private logger: LoggerService,
    public faIcons: FaIconLibrary,
    private fb: FormBuilder,
    private meService: MeService,
    private dialogService: DialogService,
    private commentService: UserStoryCommentsService,
    private userStoryService: UserStoryService,
    private toastr: ToastrService,
    private userStoryOwnerService: UserStoryOwnerService,
    private authService: AuthTokenService,
    private projectUserService: ProjectUserService
  ) {
    faIcons.addIcons(...all);
    this.form = this.fb.group(
      {
        id: [null, [Validators.required]],
        project_id: [null, [Validators.required]],
        project_name: [null, []],
        user_story_status_id: [null, [Validators.required]],
        user_story_status_description: [null, [Validators.required]],
        user_story_type_id: [null, [Validators.required]],
        // user_story_type_description: [null, [Validators.required]],
        user_story_priority_id: [null, [Validators.required]],
        // user_story_priority_description: [null, [Validators.required]],
        user_story_estimate_id: [null, [Validators.required]],
        // user_story_estimate_description: [null, [Validators.required]],
        requested_by_user: [null, []],
        requested_by_user_id: [null, []],
        owned_by_users_json: [null, []],
        owned_by_users: [null, []],
        owned_by_user_ids: [null, [Validators.required]],
        project_label_id: [null, []],
        // project_label_description: [null, []],
        accepted_at: [null, []],
        deadline: [null, []],
        updated_by_user_id: [],
        updated_by_user: []
      }
    );
    this.formHeader = this.fb.group(
      {
        title: [null, [Validators.required]],
      }
    );
    this.formComment = this.fb.group(
      {
        toPost: [null, [Validators.required]]
      });
    this.formDescription = this.fb.group({
      description: [null, [Validators.required]]
    });
  }

  //#region NG Event Handlers

  ngOnInit(): void {
    // this.logger.debug('ngOnInit...');
    // this.modUsers = this.projectUsers;

    // let tmpUsers = this.users.filter(u => this.projectUsers.some((pu: any) => u.id === pu.user_id))
    // this.logger.debug('tmpUsers', tmpUsers);
    // tmpUsers.forEach((pu) => {
    //   this.modUsers.push(CasiOwnerUser.createFromUser(pu));
    //   // this.logger.debug('ngAfterContentInit:modUsers:', this.modUsers);
    //   // let found = this.users.find((value, index, array) => {

    //   //   // this.logger.debug('try to match user ', value);
    //   //   return pu.user_id == value.id;
    //   // });
    //   // if (found)
    //   //   this.modUsers.push({ user_id: found.id, fullName: found.fullName, initials: found.initials, email: found.email });
    // });
    this.logger.debug('modusers', this.modUsers);
  }

  ngOnDestroy(): void {
    // this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  ngAfterContentInit(): void {
    // this.logger.debug('ngAfterContentInit...');
    if (this.selectedUserStoryId && this.selectedUserStoryId == this.userStory.id!.toString()) {
      this.expandedState = true;
      this.loadData();
      let token = this.authService.getToken();
      // this.logger.debug('token', token);
      this.userStoryService
        .fetchHistoricFileList(this.selectedUserStoryId)
        .then((data: string[]) => {
          data.forEach((d) => {
            this.historicFiles.push({ filename: d, url: `${apiUrl}/userstory/historicalfile/${this.selectedUserStoryId}?file=${encodeURIComponent(d)}&token=${token}` });
          });
        }).catch((error: any) => {

        })
    }

    if (this.userStory.description)
      this.edvalue = this.userStory.description;

  }

  ngAfterViewChecked(): void {

    this.setEvents();

  }

  private setEvents(): void {
    let imgs = Array.from(document.getElementsByClassName('MinImg') as HTMLCollectionOf<HTMLImageElement>);
    var Max = document.getElementById('MaxImg');
    if (imgs && Max && !this.eventsSet) {
      for (let i = 0; i < imgs.length; i++) {
        imgs[i].addEventListener('click', function () {

          // transfer the image data

          if (Max && this.src)
            Max.style.backgroundImage = "url(" + this.src + ")";

          // make the large image appear
          if (Max)
            Max.style.display = "block";
        });
      }

      Max.addEventListener('click', function () {

        // make the large image disappear
        if (Max)
          Max.style.display = "none";
      });

      this.eventsSet = true;
    }
  }

  //#endregion

  //#region Form Properties

  get f() {
    return this.form.controls;
  }
  get fIsDirty() {
    return this.form.dirty;
  }
  get fHeader() {
    return this.formHeader.controls;
  }
  get fComment() {
    return this.formComment.controls;
  }
  get fDescription() {
    return this.formDescription.controls;
  }
  // get fCode() {
  //   return this.formCode.controls;
  // }
  get titleIsDirty() {
    return this.formHeader.dirty;
  }
  get title() {
    return this.formHeader.get('title');
  }
  get project_id() {
    return this.form.get('project_id');
  }
  get project_id_value() {
    return this.form.get('project_id')?.value;
  }
  get project_name() {
    return this.form.get('project_name');
  }
  get user_story_status_id() {
    return this.form.get('user_story_status_id');
  }
  get user_story_status_id_value() {
    return this.form.get('user_story_status_id')?.value;
  }
  get user_story_status_description() {
    return this.form.get('user_story_status_description');
  }
  get user_story_status_description_value() {
    return this.form.get('user_story_status_description')?.value;
  }
  get user_story_type_id() {
    return this.form.get('user_story_type_id');
  }
  get user_story_type_id_value() {
    return this.form.get('user_story_type_id')?.value;
  }
  get user_story_type_description() {
    return this.form.get('user_story_type_description');
  }
  get user_story_priority_id() {
    return this.form.get('user_story_priority_id');
  }
  get user_story_priority_id_value() {
    return this.form.get('user_story_priority_id')?.value;
  }
  get user_story_priority_description() {
    return this.form.get('user_story_priority_description');
  }
  get user_story_priority_description_value() {
    return this.form.get('user_story_priority_description')?.value;
  }
  get user_story_estimate_id() {
    return this.form.get('user_story_estimate_id');
  }
  get user_story_estimate_id_value() {
    return this.form.get('user_story_estimate_id')?.value;
  }
  get user_story_estimate_description() {
    return this.form.get('user_story_estimate_description');
  }
  get user_story_estimate_description_value() {
    return this.form.get('user_story_estimate_description')?.value;
  }
  get requested_by_user() {
    return this.form.get('requested_by_user');
  }
  get requested_by_user_value() {
    return this.form.get('requested_by_user')?.value;
  }
  get requested_by_user_id() {
    return this.form.get('requested_by_user_id');
  }
  get requested_by_user_id_value() {
    return this.form.get('requested_by_user_id')?.value;
  }
  get owned_by_users() {
    return this.form.get('owned_by_users');
  }
  get owned_by_user_ids() {
    return this.form.get('owned_by_user_ids');
  }
  get owned_by_user_ids_value() {
    return this.form.get('owned_by_user_ids')?.value;
  }

  get description() {
    return this.formDescription.get('description');
  }
  get toPost() {
    return this.formComment.get('toPost');
  }

  //#endregion

  //#region Public functions

  public projectValueChange(value: any): void {
    this.logger.debug(`projectValueChange...`, value);
    this.setCurrentProject(value);
    // this.form.patchValue({ project_name: value.project_name });
  }

  public onClose() {
    this.logger.debug(`onClose...`);
    this.closeForm.emit();
  }

  public onExpand() {
    this.logger.debug(`onExpand...`, this.commentsFetched);
    if (!this.commentsFetched) {
      this.loadData();
    }
  }

  public onCollapse() {
    this.logger.debug(`onCollapse`);
  }

  public handleCommentDelete(userComment: CasiUserStoryComment): void {
    this.logger.debug('handleCommentDelete:userComment: ', userComment);
    this.removeUserComment(userComment);
  }

  public handleCommentSave(userComment: CasiUserStoryComment): void {
    this.logger.debug(`handleCommentSave:userComment: `, userComment);
    this.saveUserComment(userComment);
  }

  public emojiClick(evnt: any): void {
    this.logger.debug(`emojiClick...`, evnt);
  }

  public onTestChange(evnt: any): void {
    this.logger.debug(`onTestChange:evnt: `, evnt)
  }

  public onChange(value: string): void {
    this.logger.debug(`onChange ${value}`);
    // this.form.patchValue({ title: value })
  }

  public sortSelectionChange(evnt: any) {
    this.logger.debug(`sortSelectionChange ${evnt}`);
    this.sortComment(evnt);
  }

  public selectionChange(evnt: any) {
    this.logger.debug(`selectionChange: `, evnt);
  }

  private doNotify() {
    let byUser = this.meService.get()?.firstName;
    if (this.userStory.user_story_status_description === 'accepted' || this.userStory.user_story_status_description === 'rejected')
      this.userStoryOwnerService
        .notifyOwnersStatus(this.selectedOwners, this.userStory, byUser)
        .then((result: any) => {
          this.toastr.success(`Status Updated, Story Owners Notified`);
          this.refresh.emit();
        }).catch((error: any) => {

        });

    if (this.userStory.user_story_status_description === 'finished' || this.userStory.user_story_status_description === 'delivered') {
      let requestor = this.users.find(u => u.id === this.selectedRequester);
      if (requestor)
        this.userStoryOwnerService
          .notifyRequestor(requestor, this.userStory, byUser)
          .then((result: any) => {
            this.toastr.success(`Status Updated, Requestor Notified`);
            this.refresh.emit();
          }).catch((error: any) => {

          });
    }

  }

  public selectionStatusChange(evnt: any) {
    this.logger.debug(`selectionStatusChange: `, evnt);
    this.updateSelectionStatus(evnt);
  }

  public selectionPriorityChange(evnt: any) {
    this.logger.debug(`selectionPriorityChange: `, evnt);
  }

  public selectionEstimateChange(evnt: any) {
    this.logger.debug(`selectionEstimateChange: `, evnt);
  }

  public selectionRequesterChange(evnt: any) {
    this.logger.debug(`selectionRequesterChange: `, evnt);
  }

  private notifyComment(): Promise<any> {
    let uids: number[] = [];
    let users: UserModel[] = [];
    let byUser = this.meService.get()?.firstName;
    this.selectedOwners.forEach((o) => {
      if (o.user_id != this.meService.userId)
        uids.push(o.user_id);
    });
    if (this.selectedRequester && this.selectedRequester != this.meService.userId)
      uids.push(this.selectedRequester);
    uids.forEach((uid) => {
      let found = this.users.find((value) => {
        return value.id === uid;
      });
      if (found)
        users.push(found);
    });
    if (users.length > 0)
      return this.commentService.notifyComment(users, this.userStory, this.formComment.value.toPost, byUser);
    else
      return Promise.resolve(true);
  }

  public onPost(): void {
    this.logger.debug('onPost: ', this.formComment.value);
    let toSubmit = new CasiUserStoryComment({
      // id: this.lastId++,
      user_story_id: this.userStory.id,
      user_id: this.meService.userId,
      comment: this.formComment.value.toPost,
      // dateTime: new Date()
    });
    this.logger.debug('onPost: toSubmit', toSubmit);
    this.logger.debug('onPost: userStory', this.userStory);
    this.commentService.add(toSubmit)
      // .then(resp => {
      //   return this.notifyComment();
      // })
      .then(data => {
        this.commentsFetched = false;
        this.loadData();
        this.comvalue = '';
        this.formComment.patchValue({ toPost: '' });
        this.formComment.reset();
      })
      .catch(err => {
        this.logger.error(`onPost:error: `, err.message);
      })
  }

  public inlineEditClicked(item: string): void {
    if (item === inlineEditTypes.description) {
      this.readonlyDescription = false;
    } else if (item === inlineEditTypes.code) {
      this.readonlyCode = false;
    }
  }

  public cancelMainFormEdit(): void {
    this.logger.debug(`cancelMainFormEdit...`);
    this.form.reset();
    this.form.patchValue(this.userStory);
    this.resetSelection();
  }

  public cancelInlineEdit(item: string): void {
    this.logger.debug(`cancelInlineEdit...`, item);
    if (item === inlineEditTypes.description) {
      if (this.formDescription.dirty) {

        const dialogRef = this.dialogService.open({
          content: ConfirmModalComponent,
          minWidth: 320,
          width: 750,
        });

        const contentRef = dialogRef.content.instance as ConfirmModalComponent;
        contentRef.title = "ABANDON CHANGES";
        contentRef.content = `Abandon your changes?`;

        dialogRef.result.subscribe((r: any) => {
          this.logger.debug('cancelChanges:result:', r);
          if (r instanceof DialogCloseResult) {
            return;
          }
          if (r.text === CasiCloseOptions.Ok) {
            this.logger.debug("cancelChanges:ok to proceed... ");
            this.formDescription.reset();
            this.readonlyDescription = true;
            this.formDescription.patchValue(this.userStory);
          }
          this.eventsSet = false;
          setTimeout(() => this.setEvents());
        });
      } else {
        this.readonlyDescription = true;
        this.eventsSet = false;
        setTimeout(() => this.setEvents());
      }

    } else if (item === inlineEditTypes.code) {
      // if (this.formCode.dirty) {

      //   const dialogRef = this.dialogService.open({
      //     content: ConfirmModalComponent,
      //     minWidth: 320,
      //     width: 750,
      //   });

      //   const contentRef = dialogRef.content.instance as ConfirmModalComponent;
      //   contentRef.title = "ABANDON CHANGES";
      //   contentRef.content = `Abandon your changes?`;

      //   dialogRef.result.subscribe((r: any) => {
      //     this.logger.debug('cancelChanges:result:', r);
      //     if (r instanceof DialogCloseResult) {
      //       return;
      //     }
      //     if (r.text === CasiCloseOptions.Ok) {
      //       this.logger.debug("cancelChanges:ok to proceed... ");
      //       this.formCode.reset();
      //       this.readonlyCode = true;
      //       this.formCode.patchValue(this.userStory);
      //     }
      //   });
      // } else {
      //   this.readonlyCode = true;
      // }
    }
  }

  public cancelTitleEdit(): void {
    this.logger.debug(`cancelTitleEdit...`,);
    this.formHeader.reset();
    this.formHeader.patchValue(this.userStory);
  }

  public saveMainForm(): void {
    this.logger.debug(`saveMainForm...`, this.form.value);
    this.saveUserStoryChanges();
  }

  public saveInlineChanges(item: string): void {
    this.logger.debug(`saveDescriptionChanges...`, item); // this.formDescription.value);
    if (item === 'description') {
      this.userStory.description = this.formDescription.value.description;
      this.eventsSet = false;
      this.saveUserStoryChanges();
      // this.activity.description = this.form.value.description;
      this.readonlyDescription = true;
    } else if (item === 'code') {
      // this.userStory.code = this.formCode.value.code;
      // // this.activity.description = this.form.value.description;
      this.readonlyCode = true;
    }
  }

  public saveTitleChanges(): void {
    this.logger.debug(`saveTitleChanges...`, this.formHeader.value.title); // this.formDescription.value);
    this.userStory.title = this.formHeader.value.title;
    this.saveUserStoryChanges();
    this.formHeader.reset();
    this.formHeader.patchValue(this.userStory);
  }

  public cancelDescription(): void {
    this.logger.debug(`cancelDescription...`, this.form.dirty);
    if (this.form.dirty) {
    } else {
      this.readonlyDescription = true;
    }
    this.readonlyDescription = true;
  }

  public saveDescriptionChanges(): void {
    this.logger.debug(`saveDescriptionChanges...`, this.formDescription.value);
    this.userStory.description = this.formDescription.value.description;
    this.saveUserStoryChanges();
    this.readonlyDescription = true;
  }

  public onStatusButtonChange(evnt: any) {
    this.logger.debug(`onStatusButtonChange:evnt: `, evnt);
    const { original, next_options } = evnt;
    const toStoryStatus = this.findStoryStatus(next_options.nextUserStoryId);
    const dialogRef = this.dialogService.open({
      content: ConfirmModalComponent,
      minWidth: 320,
      width: 750,
    });

    const contentRef = dialogRef.content.instance as ConfirmModalComponent;
    contentRef.title = "UPDATE USER STORY STATUS";
    contentRef.content = `Are you sure you want to change the status of:<br>
    ${original.user_story_status_description}<br>
    to<br>
    ${toStoryStatus?.user_story_status_description}?`;
    contentRef.okText = 'Save';

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('cancelChanges:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("cancelChanges:ok to proceed... ", next_options);
        this.updateSelectionStatusAndSave(toStoryStatus);
      }
    });

  }

  //#endregion

  //#region Grid functions

  public sortComment(option: CommentSortOptions) {
    this.logger.debug(`sortComment: `, option);
    if (option === CommentSortOptions.oldestToNewest) {
      this.sortCommentsDesc();
    } else {
      this.sortCommentAsc();
    }
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.sortComment(this.selectedSort); //CommentSortOptions.oldestToNewest);
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    // this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.sortComment(this.selectedSort); //CommentSortOptions.oldestToNewest);
  }

  public copyStoryID(id: number): void {
    navigator.clipboard.writeText(id.toString());
    this.toastr.success(`Story ID ${id} copied to clipboard`);
  }

  public copyLink(us: CasiUserStory): void {
    this.logger.debug('us', us);
    let url = `${window.location.host}/userstory/show/${us.id}`;
    this.logger.debug('copy url', url);
    navigator.clipboard.writeText(url);
    this.toastr.success(`URL ${url} copied to clipboard`);
  }

  public onValueChange(value: any): void {
    this.logger.debug("onValueChange", value);
    let byUser = this.meService.get()?.firstName;
    if (!this.deletedOwner && this.userStory.id) { //update
      this.userStoryOwnerService.bulkSetOwners(this.selectedOwners, this.userStory, byUser)
        .then((result: any) => {
          this.toastr.success(`Story Owners Updated`);
          this.refresh.emit();
        }).catch((error: any) => {
          this.logger.error(`onValueChange:error: `, error);
        });
    }
    else
      this.deletedOwner = false; //clear
  }

  public onRemoveTag(tag: any) {
    this.logger.debug("removeTag", tag);
    if (this.userStory.id) {
      this.deletedOwner = true;
      this.userStoryOwnerService.deleteByUser(tag.dataItem.user_id, this.userStory.id)
        .then((result: any) => {
          this.toastr.success(`Story Owners Updated`);
          this.refresh.emit();
        }).catch((error: any) => {
          this.logger.error(`onValueChange:error: `, error);
        });
    }
  }

  public valueChange(value: any): void {
    // this.logger.debug(`valueChange...`, value);
    this.formDescription.patchValue({ description: value });
  }

  public commentValueChange(value: any): void {
    // this.logger.debug(`valueChange...`, value);
    this.formComment.patchValue({ toPost: value });
    this.comvalue = value;
  }

  public openImageUpload() {
    setTimeout(() => this.dialog.open(), 200);
  }

  public onImageLoaded(args: any): void {
    this.logger.debug('onImageLoaded...', args);
    const { data } = args;
  }

  //#endregion

  //#region Private functions

  private updateSelectionStatus(evnt: any, doId: boolean = false) {
    const { id, nextUserStoryId, user_story_status_description, userStoryAction } = evnt;
    if (doId) {
      this.form.patchValue({
        user_story_status_id: id ? id : nextUserStoryId
      });
    }
    this.form.patchValue({
      user_story_status_description: user_story_status_description ? user_story_status_description : userStoryAction
    });
    this.selectedStatus = id ? id : nextUserStoryId;
    this.setSelectedStoryStatus(this.selectedStatus!);
    if ((user_story_status_description ? user_story_status_description : userStoryAction).toLowerCase() === 'unscheduled' || (user_story_status_description ? user_story_status_description : userStoryAction).toLowerCase() === 'icebox') {
      this.removeValidationToOwners();
    } else {
      this.addValidationToOwners();
    }
  }

  private updateSelectionStatusAndSave(args: any) {
    this.logger.debug(`updateSelectionStatusAndSave:args: `, args);
    const { id, user_story_status_description } = args;
    this.form.patchValue({
      user_story_status_id: id,
      user_story_status_description: user_story_status_description
    });
    this.saveUserStoryChanges()
  }

  private addValidationToOwners() {
    this.owned_by_user_ids?.setValidators([Validators.required]);
    this.owned_by_user_ids?.updateValueAndValidity();
  }

  private removeValidationToOwners() {
    this.owned_by_user_ids?.setValidators(null);
    this.owned_by_user_ids?.updateValueAndValidity();
  }

  private setListView(): void {
    if (this.rawData) {
      this.listView = undefined;
      setTimeout(() => {
        this.listView = <ListViewDataResult>{ data: this.rawData, total: this.rawData.length };
        this.logger.debug(`setListView:listView: `, this.listView);
      });
    }
  }

  private sortCommentsDesc(): void {
    if (this.rawData)
      this.rawData = this.rawData.sort((a, b) => a.id! - b.id!);
    this.setListView();
  }

  private sortCommentAsc(): void {
    if (this.rawData)
      this.rawData = this.rawData.sort((a, b) => b.id! - a.id!);
    // }
    this.setListView();
  }

  private loadData(): void {
    this.logger.debug('loadData...', this.userStory);
    if (!this.commentsFetched) {
      this.commentService.get(this.userStory!.id!)
        .subscribe({
          next: resData => {
            this.rawData = resData;
            // this.loadData();
            this.sortComment(this.selectedSort); //CommentSortOptions.oldestToNewest);
            this.commentsFetched = true;
          },
          error: err => {
            this.logger.error(`comments:error: `, err.message);
            this.rawData = [];
            // this.loadData();
            this.sortComment(this.selectedSort); //CommentSortOptions.oldestToNewest);
          }
        });
    }
  }

  private setStoryIcons(): any {
    // this.logger.debug(`setStoryIcons...storyTypes:`, this.storyTypes);
    // this.logger.debug(`setStoryIcons...selectedType`, this.selectedType);
    if (this.storyTypes) {
      let found = this.storyTypes.find(st => st.id === this.selectedType);
      if (found) {
        this.iconList = [('fad' as IconPrefix), (found.icon as IconName)];
        setTimeout(() => {
          this.selectTypeIconClass = found.iconClass;
        });
      }
      // this.logger.debug(`setStoryIcons: selectTypeIcons`, this.selectTypeIconClass);
    }
  }

  private findUserComment(userComment: CasiUserStoryComment): CasiUserStoryComment | undefined {
    // return undefined;
    return this.rawData?.find(a => a.id === userComment.id);
  }

  private saveUserComment(userComment: CasiUserStoryComment): void {
    this.logger.debug(`saveUserComment:userComment: `, userComment);
    let found = this.findUserComment(userComment); //  this.userStory.activitys?.find(a => a.id === userComment.id);
    if (found) {
      this.logger.debug(`sveUserComment:found: `, found);
      found = Object.assign(found, userComment);
      this.logger.debug(`sveUserComment:found: updated: `, found);
      this.commentService.update(userComment)
        .then(resp => {
          this.listView?.data.map((value) => {
            if (value.id === userComment.id && resp.updatedAt)
              value.updatedAt = resp.updatedAt;
          })
        })
        .catch(err => {
          this.logger.error(`saveUserComment:error: `, err.message);
        });
    }
    // this.loadData();
  }

  private removeUserComment(userComment: CasiUserStoryComment): void {
    this.logger.debug(`removeUserComment:userComment: `, userComment);
    this.commentsFetched = false;
    this.commentService.destroy(userComment.id!)
      .then(resp => {
        this.logger.debug(`removeUserComment:resp: `, resp);
        this.loadData();
      })
      .catch(err => {
        this.logger.error(`removeUserComment: `, err.message);
      });
  }

  private saveUserStoryChanges() {
    let toSave = this.buildToSave();
    this.logger.debug(`saveUserStoryChanges...`, toSave);
    this.userStoryService.update(toSave)
      .then(resp => {
        this.setStoryIcons();
        this.notify('Changes saved.')
        this.form.reset();
        this.formHeader.reset();
        this.formDescription.reset();
        this.userStory = resp;
      })
      .catch(err => {
        this.notify('Problem saving changes', true);
      });
  }

  private buildToSave(): CasiUserStory {
    this.logger.debug(`buildToSave...`, this.form.value, this.formDescription.value, this.formHeader);
    let toSave = {
      ...this.form.value, ...this.formDescription.value,
      ...this.formHeader.value, ...{
        overrideUserIds: true
      }
    };
    this.logger.debug(`buildToSave: toSave: `, toSave);
    return new CasiUserStory(toSave);
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private resetSelection(): void {
    this.selectedType = this._userStory!.user_story_type_id!;
    this.selectedPriority = this._userStory!.user_story_priority_description!;
    this.selectedRequester = this._userStory!.requested_by_user_id!;
    this.selectedProjectId = this._userStory!.project_id;
    this.selectedOwners = this._userStory!.owned_by_users_json!;
    this.selectedEstimate = this._userStory!.user_story_estimate_id;
    this.selectedStatus = this._userStory!.user_story_status_id!;
    if (this.selectedStatus === 6) {
      this.removeValidationToOwners();
    }
    this.setSelectedStoryStatus(this.selectedStatus);
    this.selectTypeIconClass = this.setStoryIcons();
    this.fetchProjectUsers(this._userStory?.project_id);
  }

  private setProjectLabels(vals: CasiProjectLabel[]) {
    // this.logger.debug(`setProjectLabels:val: `, vals);
    if (this.userStory && this.userStory.project_id) {
      this._projectLables = vals.filter((p) => p.project_id === this.userStory?.project_id);
    } else {
      this._projectLables = vals;
    }
    // this.logger.debug(`setProjectLables: `, this.projectLabels);
  }

  private setCurrentProject(project?: Project) {
    this.logger.debug(`setCurrentProject: `, project);
    if (project && project.id) {
      this.userStoryProject = project;
      this.fetchProjectUsers(project.id);
    } else {
      this.userStoryProject = undefined;
    }
    this.setProjectLabels(this.allProjectLabels);
  }

  private fetchProjectUsers(projectId: number | undefined): void {
    if (projectId)
      this.projectUserService.getByProjectId(projectId)
        .then(resp => {
          this.modUsers = resp.map((r: any) => CasiOwnerUser.createFromProjectUser(r));
        })
        .catch(err => {
          this.logger.debug(`fetchProjectUsers: `, err.message);
        })
  }

  private findStoryStatus(id: number) {
    return this.storyStatuses.find(ss => ss.id === id);
  }

  private setSelectedStoryStatus(id: number) {
    this.selectedStoryStatus = this.findStoryStatus(id); // this.storyStatuses.find(ss => ss.id === id);
    this.logger.debug(`setSelectedStoryStatus: `, this.selectedStoryStatus);
  }

  //#endregion

}
